// src/Pages/TerminalManagement.jsx (REFACTORIZADO)
import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api/api.js';
import TerminalForm from '../Components/TerminalForm.js';
import TerminalList from '../Components/TerminalList.js';
import { useToast } from '../Components/ui/Toast.jsx';
import { useConfirm } from '../Components/ui/ConfirmDialog.jsx';
import styles from './TerminalManagement.module.css';

// Usamos null/vacío para los valores iniciales para mayor claridad
const INITIAL_FORM_STATE = { id: null, name: '', address: '', tel_number: '' };

const TerminalManagement = () => {
  const [terminals, setTerminals] = useState([]);
  const [form, setForm] = useState(INITIAL_FORM_STATE);
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useToast();
  const { confirm } = useConfirm();

  // --- Data Fetching (Función optimizada con useCallback) ---
  const fetchTerminals = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await api.get('/list_terminal');
      setTerminals(res.data || []);
    } catch (err) {
      showToast('Error al cargar terminales. Verifica la consola para más detalles.', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchTerminals();
  }, [fetchTerminals]);

  // --- Mutaciones y Handlers ---

  // 💡 FUNCIÓN CLAVE: Actualiza el estado local de la lista (evita re-fetch)
  const handleUpdateTerminalList = (updatedTerminal) => {
    setTerminals(prevTerminals => {
      if (updatedTerminal.id) {
        // Edición (Encuentra y reemplaza)
        return prevTerminals.map(terminal =>
          terminal.id === updatedTerminal.id ? { ...terminal, ...updatedTerminal } : terminal
        );
      }
      // Creación (Añade al inicio)
      return [updatedTerminal, ...prevTerminals];
    });
  };

  const handleDelete = useCallback(async (id) => {
    const ok = await confirm('¿Eliminar esta terminal? Esta acción no se puede deshacer.', {
      title: 'Confirmar eliminación',
      confirmText: 'Eliminar',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(`/terminal/${id}`);

      // Actualización local (instantáneo)
      setTerminals(prevTerminals => prevTerminals.filter(t => t.id !== id));

      // Si el elemento eliminado estaba siendo editado, limpia el formulario
      if (Number(id) === form.id) setForm(INITIAL_FORM_STATE);

      showToast('Terminal eliminada correctamente.', { type: 'success' });
    } catch (err) {
      showToast('No se pudo eliminar la terminal.', { type: 'error' });
    }
  }, [form.id, confirm, showToast]);

  const formRef = useRef(null);

  const handleEdit = useCallback((terminal) => {
    setForm({
      id: terminal.id,
      name: terminal.name,
      address: terminal.address,
      tel_number: terminal.tel_number
    });
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }, []);

  const resetForm = useCallback(() => {
    setForm(INITIAL_FORM_STATE);
  }, []);

  // --- Renderizado ---

  return (
    <div className={styles.container}>
      <h2>Gestión de Terminales</h2>

      {/* 1. Formulario (Delegado) */}
      <div ref={formRef}>
      <TerminalForm
        form={form}
        setForm={setForm}
        resetForm={resetForm}
        onSuccess={handleUpdateTerminalList}
      />
      </div>
      
      <h3>Lista de Terminales</h3>
      
      {isLoading && <div className={styles.loadingMessage}>Cargando terminales...</div>}

      {/* 2. Lista/Tabla (Delegada) */}
      {!isLoading && (
        <TerminalList
          terminals={terminals}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
};

export default TerminalManagement;
